import { useEffect, useMemo, useRef, useState } from "react";
import { useAppSelector as useSelector } from "../../../../store";
import Chevron from "../../../UI/Icons/Chevron";
import Search from "../../../UI/Icons/Search";

const CandidatesFilter = ({
  selectedCandidateIds,
  setSelectedCandidateIds,
}: {
  selectedCandidateIds: number[];
  setSelectedCandidateIds: (ids: number[]) => void;
}) => {
  const allCandidatesList = useSelector(
    (state) => state.timesheet.allCandidates
  );
  const [openDropdown, setOpenDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectAllRef = useRef<HTMLInputElement | null>(null);

  const filteredCandidates = allCandidatesList.filter((item) =>
    item.full_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const allIds = useMemo(
    () => allCandidatesList.map((c) => c.id),
    [allCandidatesList]
  );
  const isAllSelected = selectedCandidateIds.length === allIds.length;
  const isIndeterminate = selectedCandidateIds.length > 0 && !isAllSelected;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdown(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keep indeterminate state in sync
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const toggleCandidate = (id: number) => {
    const exists = selectedCandidateIds.includes(id);
    const newSelected = exists
      ? selectedCandidateIds.filter((i) => i !== id)
      : [...selectedCandidateIds, id];
    setSelectedCandidateIds(newSelected);
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedCandidateIds([]);
    } else {
      setSelectedCandidateIds(allIds);
    }
  };

  const getLabel = () => {
    if (isAllSelected) return "All";
    if (selectedCandidateIds.length === 0) return "All";
    return `${selectedCandidateIds.length} Selected`;
  };

  return (
    <>
      {allCandidatesList.length > 0 && (
        <>
          {/* Toggle Dropdown */}
          <div
            className="d-flex align-items-center gap-3 cursor-pointer"
            onClick={() => setOpenDropdown((prev) => !prev)}
          >
            <div className="fs-13 fw-700 text-black">{getLabel()}</div>
            <Chevron
              size={16}
              isOpen={openDropdown}
              // fill={openDropdown ? "black" : "#AAA"}
            />
          </div>

          {openDropdown && (
            <div
              className="position-absolute top-100 end-0 z-10 bg-teal-pale border border-black shadow-md w-266px rounded z-1"
              ref={dropdownRef}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Search */}
              <div className=" p-6px">
                <div className="form-group w-100 input-icon icon-left">
                  <Search />
                  <input
                    type="text"
                    className="form-control fs-16 fw-400"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              {/* Select All */}
              <div
                className="px-15px py-8px d-flex align-items-center hover-bg-off-white"
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.preventDefault();
                  handleSelectAll();
                }}
              >
                <div className="theme-check">
                  <input
                    type="checkbox"
                    className="theme-check-input"
                    id="selectAll"
                    readOnly
                    checked={isAllSelected}
                    ref={selectAllRef}
                  />
                  <label
                    htmlFor="selectAll"
                    className="theme-check-label lh-24px fw-700 fs-16"
                  >
                    Select/Deselect All
                  </label>
                </div>
              </div>

              {/* List */}
              <div className="max-h-310px overflow-auto">
                {filteredCandidates.length > 0 ? (
                  filteredCandidates.map((candidate) => (
                    <div
                      key={candidate.id}
                      className="border-top border-black px-15px py-8px d-flex align-items-center hover-bg-off-white text-break"
                      style={{ cursor: "pointer" }}
                      onClick={(e) => {
                        e.preventDefault();
                        toggleCandidate(candidate.id);
                      }}
                    >
                      <div className="theme-check">
                        <input
                          type="checkbox"
                          className="theme-check-input"
                          id={`candidate-${candidate.id}`}
                          readOnly
                          checked={selectedCandidateIds.includes(candidate.id)}
                        />
                        <label
                          htmlFor={`candidate-${candidate.id}`}
                          className="theme-check-label lh-24px fs-16 fw-400"
                        >
                          {candidate.full_name}
                        </label>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center text-grey-dark py-2">
                    No candidates found
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default CandidatesFilter;
